import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Scale, AlertCircle, Shield, CheckCircle, MessageSquare } from 'lucide-react';
import Navbar from '@/components/trinity/Navbar';
import Footer from '@/components/trinity/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import PageMeta from '@/components/seo/PageMeta';
import TermsCredentialNotice from '@/components/trinity/TermsCredentialNotice';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PageMeta path="/terms-of-service" />
      <Navbar />
      
      <div className="container mx-auto px-6 py-16 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-slate-100 rounded-full px-6 py-3 mb-6">
              <Scale className="w-6 h-6 text-slate-600" />
              <span className="text-slate-900 font-semibold">Legal Agreement</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-lg text-slate-600">
              Effective Date: February 14, 2026 | Last Updated: November 4, 2026
            </p>
          </div>

          {/* Introduction */}
          <Card className="mb-8">
            <CardContent className="p-8">
              <p className="text-slate-700 leading-relaxed mb-4">
                Welcome to Trinity Dental Arts. These Terms of Service ("Terms") govern your use of our website 
                located at trinitydentalarts.com and the services we provide. By accessing or using our website, 
                you agree to be bound by these Terms and our Privacy Policy.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Please read these Terms carefully before using our services. If you do not agree with these Terms, 
                you should not use our website or services.
              </p>
            </CardContent>
          </Card>

          {/* Main Sections */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </div>
                Acceptance of Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                By accessing and using this website, you accept and agree to be bound by the terms and provision 
                of this agreement. If you do not agree to these Terms, please do not use this website.
              </p>
              <p className="text-slate-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon 
                posting to the website. Your continued use of the website after changes are posted constitutes your 
                acceptance of the modified Terms.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                Use of Website
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                You may use our website for lawful purposes only. You agree not to use the website:
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">In any way that violates any applicable federal, state, local, or international law or regulation</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">To transmit any advertising or promotional material, spam, or unsolicited communications</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">To impersonate or attempt to impersonate Trinity Dental Arts, our employees, or any other person or entity</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">To engage in any conduct that restricts or inhibits anyone's use or enjoyment of the website</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">To use any robot, spider, or other automatic device to monitor or copy our website without prior written permission</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">To introduce any viruses, malware, or other malicious code</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                Intellectual Property Rights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                The website and its entire contents, features, and functionality (including but not limited to all 
                information, software, text, displays, images, video, and audio) are owned by Trinity Dental Arts, 
                its licensors, or other providers of such material and are protected by United States and international 
                copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
              </p>
              <p className="text-slate-700 leading-relaxed">
                You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly 
                perform, republish, download, store, or transmit any of the material on our website without our 
                prior written consent.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Patient-Provider Relationship</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                The information provided on this website is for general informational purposes only and does not 
                constitute medical or dental advice. It is not intended to create a patient-dentist relationship.
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">No patient-dentist relationship is established by using this website or communicating with us electronically</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Always seek the advice of your dentist or other qualified healthcare provider with any questions about your dental health</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Never disregard professional medical advice or delay seeking it because of something you have read on this website</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">If you think you may have a dental emergency, call your dentist or 911 immediately</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <TermsCredentialNotice />

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Appointment Booking and Cancellation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                When you book an appointment through our website:
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Appointments are subject to availability and confirmation by our office</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">We may contact you to confirm or reschedule your appointment</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">You agree to provide accurate contact and insurance information</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Non-surgical visits require 48 hours’ notice; surgical visits require 5 business days’ notice. Late cancellations and missed visits may be subject to fees. Full-arch surgery has additional pre-operative and cancellation terms in the schedule signed at booking. See our <Link to="/resources/office-policies#cancellations" className="text-blue-600 hover:text-blue-700 underline">Office Policies</Link> and your signed policy for details.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Missed appointments without prior notice may result in a fee</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Payment and Billing</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                By using our services, you agree to the following payment terms:
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Payment is due when scheduling. For plans over $200, a 50% deposit reserves your appointment and the balance is due before your visit date, unless other arrangements have been made.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">We accept cash, check, credit cards, and offer financing options</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Insurance claims will be filed as a courtesy, but you are ultimately responsible for all charges</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Outstanding balances may be subject to interest charges and collection efforts</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Treatment cost estimates are approximate and may change based on actual procedures performed</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                SMS Text Messaging Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                By providing your phone number and consenting to receive SMS text messages from Trinity Dental Arts, you agree to the following terms:
              </p>
              
              <div className="space-y-4 mb-4">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Types of Messages</h4>
                  <p className="text-slate-700 leading-relaxed">
                    You may receive the following types of SMS text messages from Trinity Dental Arts:
                  </p>
                  <ul className="space-y-2 mt-2">
                    <li className="flex gap-3">
                      <span className="text-blue-600 mt-1">•</span>
                      <span className="text-slate-700">Appointment reminders and confirmations</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-blue-600 mt-1">•</span>
                      <span className="text-slate-700">Marketing and promotional messages about our services and special offers</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-blue-600 mt-1">•</span>
                      <span className="text-slate-700">General two-way communication regarding your dental care</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-blue-600 mt-1">•</span>
                      <span className="text-slate-700">Important practice updates and announcements</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Texting Cadence</h4>
                  <p className="text-slate-700 leading-relaxed">
                    Message frequency varies depending on your appointment schedule and engagement with our practice. 
                    You may receive anywhere from 1-10 messages per month. Msg&data rates may apply.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Message and Data Rates</h4>
                  <p className="text-slate-700 leading-relaxed">
                    Standard message and data rates may apply based on your mobile carrier's plan. Trinity Dental Arts 
                    does not charge for text messages, but your carrier may charge for sending and receiving messages. 
                    Contact your mobile carrier for details about your messaging plan.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Privacy Policy</h4>
                  <p className="text-slate-700 leading-relaxed">
                    Your phone number and text message communications are handled in accordance with our{' '}
                    <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-700 underline">
                      Privacy Policy
                    </Link>. We do not share your phone number with third parties for marketing purposes. 
                    See our privacy policy for more information on how we protect and use your personal information.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2">Opt-Out Instructions</h4>
                  <p className="text-slate-700 leading-relaxed mb-2">
                    You can opt out of receiving SMS text messages from Trinity Dental Arts at any time by:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex gap-3">
                      <span className="text-blue-600 mt-1">•</span>
                      <span className="text-slate-700"><strong>Reply STOP</strong> to any text message to opt out of all future messages</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-blue-600 mt-1">•</span>
                      <span className="text-slate-700"><strong>Reply HELP</strong> to receive support and additional information</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="text-blue-600 mt-1">•</span>
                      <span className="text-slate-700">Contact our office at (727) 228-6846 to update your communication preferences</span>
                    </li>
                  </ul>
                  <p className="text-slate-700 leading-relaxed mt-2 text-sm">
                    After opting out, you will receive one final confirmation message. Opting out will not affect 
                    appointment reminders sent through other channels or your ability to receive dental care.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mt-4">
                <p className="text-slate-800 font-medium">
                  By opting in, you provide express written consent to receive SMS text messages from Trinity Dental Arts as described in these terms. Providing your mobile number alone does not constitute consent.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                Disclaimer of Warranties
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                THE WEBSITE AND SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, 
                EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span className="text-slate-700">The accuracy, completeness, or usefulness of any information on the website</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span className="text-slate-700">That the website will be uninterrupted, secure, or error-free</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span className="text-slate-700">That defects will be corrected</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-amber-600 mt-1">•</span>
                  <span className="text-slate-700">That the website is free from viruses or other harmful components</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Limitation of Liability</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                TO THE FULLEST EXTENT PERMITTED BY LAW, TRINITY DENTAL ARTS SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER 
                INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES 
                RESULTING FROM:
              </p>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Your access to or use of or inability to access or use the website</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Any conduct or content of any third party on the website</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Any content obtained from the website</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-blue-600 mt-1">•</span>
                  <span className="text-slate-700">Unauthorized access, use, or alteration of your transmissions or content</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Indemnification</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">
                You agree to defend, indemnify, and hold harmless Trinity Dental Arts, its affiliates, licensors, 
                and service providers, and its and their respective officers, directors, employees, contractors, 
                agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, 
                damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) 
                arising out of or relating to your violation of these Terms or your use of the website.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Third-Party Links</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                Our website may contain links to third-party websites or services that are not owned or controlled 
                by Trinity Dental Arts. We have no control over, and assume no responsibility for, the content, 
                privacy policies, or practices of any third-party websites or services.
              </p>
              <p className="text-slate-700 leading-relaxed">
                We strongly advise you to read the terms and conditions and privacy policies of any third-party 
                websites or services that you visit.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Governing Law and Jurisdiction</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the State of Florida, 
                United States, without regard to its conflict of law provisions.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Matters that must or may be brought before a court will be heard in a court with jurisdiction
                serving Pasco County, Florida.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Dispute Resolution</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed mb-4">
                If a dispute arises between you and Trinity Dental Arts, we encourage you to contact us first to
                seek a resolution. We will attempt to resolve disputes informally and in good faith. Either party
                may consult or involve legal counsel in resolving the dispute.
              </p>
              <p className="text-slate-700 leading-relaxed">
                If we cannot resolve the dispute informally, disputes will be resolved through binding arbitration,
                except where prohibited by law.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Severability</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">
                If any provision of these Terms is held to be unenforceable or invalid, such provision will be 
                changed and interpreted to accomplish the objectives of such provision to the greatest extent 
                possible under applicable law, and the remaining provisions will continue in full force and effect.
              </p>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Entire Agreement</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and 
                Trinity Dental Arts regarding the use of the website and supersede all prior and contemporaneous 
                understandings, agreements, representations, and warranties.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="bg-gradient-to-br from-slate-50 to-blue-50">
            <CardContent className="p-8">
              <h3 className="text-xl font-semibold text-slate-900 mb-4">Questions About These Terms?</h3>
              <p className="text-slate-700 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-2 text-slate-700">
                <p><strong>Trinity Dental Arts</strong></p>
                <p>11105 Trinity Blvd, Trinity, FL 34655</p>
                <p>Phone: <a href="tel:7272286846" className="text-blue-600 hover:text-blue-700">(727) 228-6846</a></p>
                <p>Email: <a href="mailto:info@trinitydentalarts.com" className="text-blue-600 hover:text-blue-700">info@trinitydentalarts.com</a></p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
}